import React, { useState } from "react";
import { api } from "../lib/api";
import { Play, CheckCircle, XCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const LANG_LABEL = { python: "Python", javascript: "JavaScript", c: "C", cpp: "C++" };

export default function CodingPanel({ question, value, onChange, attemptMode = false, attemptId, onTestResult }) {
  const language = value?.language || question.language || "python";
  const code = value?.code ?? (question.starter_code || "");
  const [stdin, setStdin] = useState("");
  const [output, setOutput] = useState(null);
  const [testResults, setTestResults] = useState(null);
  const [busy, setBusy] = useState(false);

  const setCode = (c) => onChange({ language, code: c });

  const run = async () => {
    setBusy(true);
    setOutput(null);
    try {
      const r = await api.post("/code/execute", { language, code, stdin });
      setOutput(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Run failed");
    } finally {
      setBusy(false);
    }
  };

  const runTests = async () => {
    if (!question.test_cases?.length) { toast.error("No test cases"); return; }
    setBusy(true);
    setTestResults(null);
    try {
      const visible = question.test_cases;
      const r = await api.post("/code/run-tests", { language, code, test_cases: visible });
      setTestResults(r.data);
      onTestResult && onTestResult({ passed: r.data.passed, total: r.data.total, results: r.data.results });
      toast.success(`${r.data.passed}/${r.data.total} tests passed`);
    } catch (e) {
      toast.error("Test run failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="coding-panel">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">
          Language: {LANG_LABEL[language] || language}
        </div>
        <div className="flex gap-2">
          <button
            onClick={run}
            disabled={busy}
            className="border border-zinc-300 px-3 py-1.5 text-xs font-medium hover:bg-zinc-50 inline-flex items-center gap-1.5 disabled:opacity-50"
            data-testid="code-run-btn"
          >
            <Play size={12} weight="fill" /> Run
          </button>
          {question.test_cases?.length > 0 && (
            <button
              onClick={runTests}
              disabled={busy}
              className="bg-zinc-900 text-white px-3 py-1.5 text-xs font-medium hover:bg-zinc-800 disabled:opacity-50"
              data-testid="code-run-tests-btn"
            >
              {busy ? "Running…" : "Run Tests"}
            </button>
          )}
        </div>
      </div>

      <textarea
        value={code}
        onChange={(e) => setCode(e.target.value)}
        rows={14}
        spellCheck={false}
        className="w-full font-mono text-sm bg-zinc-950 text-zinc-100 p-4 focus:outline-none focus:ring-1 focus:ring-blue-500 leading-relaxed"
        style={{ tabSize: 2 }}
        data-testid="code-editor"
        placeholder="// write your code here"
      />

      {!attemptMode && (
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mb-1.5 font-semibold">Custom stdin</div>
          <textarea
            value={stdin}
            onChange={(e) => setStdin(e.target.value)}
            rows={2}
            className="w-full font-mono text-sm border border-zinc-300 p-2 focus:border-zinc-900 focus:outline-none"
            placeholder="Optional input"
          />
        </div>
      )}

      {output && (
        <div className="border border-zinc-200" data-testid="code-output">
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold flex justify-between">
            <span>Output</span>
            <span className={output.code === 0 ? "text-green-700" : "text-red-700"}>
              Exit: {output.code}
            </span>
          </div>
          <pre className="p-3 text-xs font-mono whitespace-pre-wrap">{output.stdout || "(no stdout)"}</pre>
          {output.stderr && (
            <pre className="p-3 text-xs font-mono whitespace-pre-wrap text-red-700 border-t border-zinc-200">{output.stderr}</pre>
          )}
        </div>
      )}

      {testResults && (
        <div className="border border-zinc-200" data-testid="test-results">
          <div className="px-3 py-2 bg-zinc-50 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-semibold flex justify-between">
            <span>Test Cases</span>
            <span className="font-mono-tab">{testResults.passed} / {testResults.total} passed</span>
          </div>
          {testResults.results.map((r, i) => (
            <div key={i} className="p-3 text-xs border-b border-zinc-200 last:border-b-0 flex items-start gap-3">
              {r.passed ? <CheckCircle size={16} weight="fill" className="text-green-600 mt-0.5" /> : <XCircle size={16} weight="fill" className="text-red-600 mt-0.5" />}
              <div className="flex-1">
                <div className="font-medium">Test {i + 1} {r.hidden ? "(hidden)" : ""}</div>
                {!r.hidden && (
                  <div className="mt-1 grid grid-cols-3 gap-2 font-mono text-[11px]">
                    <div><span className="text-zinc-500">Input:</span> <code>{r.stdin || "—"}</code></div>
                    <div><span className="text-zinc-500">Expected:</span> <code>{r.expected}</code></div>
                    <div><span className="text-zinc-500">Got:</span> <code>{r.stdout || "—"}</code></div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
